import { TeamMemberForm } from "./team-member-form";
import { RegisterTeamMemberDto } from "../../../types/register-team-member.types";
import { TeamMembersAPI } from "../../../APIs/team_members.api";
import { useTeam } from "../../../context/TeamContext";
import { useToast } from "../../../components/toast";
import { useState } from "react";
import { cleanObject } from "../../../utils/clean-object.utils";
import { PersonalInfoApiError } from "../../../types/personal_info.types";

export interface ITMemberAddProps {
  onCancel: () => void;
  onSuccess?: (credentials: {
    firstName: string;
    lastName: string;
    username: string;
    password: string;
  }) => void;
}

export function TeamMemberAdd({ onCancel, onSuccess }: ITMemberAddProps) {
  const [formData, setFormData] = useState<RegisterTeamMemberDto>({
    personal_info: {
      dni: "",
      licence_number: "",
      first_name: "",
      last_name: "",
      birth_date: undefined,
      email: "",
      primary_phone_number: "",
      secondary_phone_number: "",
      emergency_contact_number: "",
      gender: undefined,
      blood_type: "",
    },
    addresses: [
      {
        address_line: "",
        city: "",
        state: "",
        country: "",
      },
    ],
    is_helmsman: false,
  });
  const { getTeam } = useTeam();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [apiErrors, setApiErrors] = useState<PersonalInfoApiError>({
    dni: null,
    email: null,
    licence_number: null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLElement>, index?: number) => {
    setApiErrors({
      dni: null,
      email: null,
      licence_number: null,
    });
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value, type } = target;

    setFormData((prev) => {
      if (!prev) return prev;

      if (type === "checkbox") {
        return {
          ...prev,
          [name]: (target as HTMLInputElement).checked,
        };
      }

      if (index !== undefined && prev.addresses) {
        return {
          ...prev,
          addresses: prev.addresses.map((address, i) =>
            i === index ? { ...address, [name]: value } : address
          ),
        };
      }

      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        const parentKey = parent as keyof RegisterTeamMemberDto;
        const parentObject = prev[parentKey];

        if (parentObject && typeof parentObject === "object") {
          return {
            ...prev,
            [parent]: {
              ...parentObject,
              [child]: value,
            },
          };
        }

        return {
          ...prev,
          [parent]: {
            [child]: value,
          },
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const data = cleanObject(formData);
      const response = await TeamMembersAPI.registerTeamMember(
        data as RegisterTeamMemberDto
      );

      if (response.statusCode === 201) {
        getTeam();
        addToast({
          message: "¡Miembro agregado al equipo!",
          type: "success",
          location: "bottom-center",
        });
        if (response.data?.user.username && response.data?.user.password) {
          onSuccess?.({
            firstName: response.data?.personal_info.first_name,
            lastName: response.data?.personal_info.last_name,
            username: response.data.user.username,
            password: response.data.user.password,
          });
        }
      } else {
        const errorCode = response.additionalData?.errorCode;
        if (errorCode === "DNI_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            dni: "Este DNI ya está en uso.",
          }));
        } else if (errorCode === "EMAIL_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            email: "Este correo electrónico ya está en uso.",
          }));
        } else if (errorCode === "LICENSE_NUMBER_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            licence_number: "Este número de licencia ya está en uso.",
          }));
        }
        addToast({
          message: response.message,
          type: "error",
          location: "bottom-center",
          duration: 10000,
        });
      }
    } catch (error) {
      addToast({
        message: "Error al agregar miembro, intente nuevamente",
        type: "error",
        location: "bottom-center",
        duration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <TeamMemberForm
      buttonText={"Agregar"}
      createTeamMember={formData}
      handleChange={handleChange}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      personalInfoApiError={apiErrors}
    />
  );
}
